/**
 * 설정 > 계정 상세.
 * Figma 기본정보·메뉴권한·활동로그 — adminUserService로 조회·저장·보안 액션.
 */
import {
  useAdminUserAuditLogs,
  useAdminUserDetail,
  useAdminUserMenuPermissions,
  useRemoveAdminUser,
  useResetAdminUserTemporaryPassword,
  useSaveAdminUserMenuPermissions,
  useUnlockAdminUser,
  useUpdateAdminUser,
  useUserAccessLogList,
} from '@/entities/admin-user/hooks/useAdminUsers';
import { adminUserService } from '@/entities/admin-user/api/admin-user.api';
import type { AdminAuditLog } from '@/entities/admin-user/types';
import {
  formatAccessLogLocation,
  type UserAccessLog,
} from '@/entities/admin-user/api/access-log.api';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ToolbarRow } from '@/shared/components/ui/row';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import {
  getRoleBadgeVariant,
  getRoleDisplayLabel,
  isMaster,
  normalizeAuthRole,
  type AuthRoleCode,
} from '@/shared/utils/authRoles';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useAuthStore } from '@/stores/authStore';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Info, Lock } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { AccountDeleteConfirmModal } from './AccountDeleteConfirmModal';
import { AccountLockedBanner } from './AccountLockedBanner';
import { PasswordResetConfirmModal } from './PasswordResetConfirmModal';
import { PasswordResetResultModal } from './PasswordResetResultModal';
import { AuditChangesDetail } from './AuditChangesDetail';
import {
  formatAuditChangesSummary,
  getAuditEventLabel,
  type AuditEventType,
} from './settingsAuditLogData';
import {
  ADMIN_MENU_CATALOG,
  applyMenuPermissionChange,
  formatAccountLockedBanner,
  formatAdminDateTime,
  getAccountStatusBadgeVariant,
  getAccountStatusLabel,
  isMenuPermissionExemptRole,
  isOptimisticLockConflict,
  mapAdminUserDetailToRow,
  OPTIMISTIC_LOCK_CONFLICT_MESSAGE,
  toAdminApiRoleName,
  toMenuPermissionSaveItems,
  type AdminAccountStatus,
  type MenuAccessLevel,
  type MenuPermissionRow,
} from './settingsAccountData';

type DetailTab = 'basic' | 'permissions' | 'activity';

const DETAIL_TABS: { key: DetailTab; label: string }[] = [
  { key: 'basic', label: '기본 정보' },
  { key: 'permissions', label: '메뉴 권한' },
  { key: 'activity', label: '활동 로그' },
];

/** 생성·수정 기본 옵션 — MASTER는 현재 역할일 때만 읽기 전용으로 노출 */
const EDITABLE_ROLE_OPTIONS: { value: AuthRoleCode; label: string }[] = [
  { value: 'ROLE_MANAGER', label: '관리' },
  { value: 'ROLE_GENERAL', label: '일반' },
];

type BasicFormState = {
  name: string;
  phone: string;
  department: string;
  /** 편집 UI는 활성/비활성만 */
  enabled: boolean;
  role: AuthRoleCode | string;
};

const toBasicForm = (detail: {
  name: string;
  phone: string | null;
  department: string | null;
  displayStatus: AdminAccountStatus;
  roleName: string;
}): BasicFormState => ({
  name: detail.name,
  phone: detail.phone?.trim() ?? '',
  department: detail.department?.trim() ?? '',
  enabled: detail.displayStatus !== 'INACTIVE',
  role: normalizeAuthRole(detail.roleName) || detail.roleName,
});

const fieldLabel = (key: keyof BasicFormState): string => {
  switch (key) {
    case 'name':
      return '이름';
    case 'phone':
      return '연락처';
    case 'department':
      return '소속';
    case 'enabled':
      return '상태';
    case 'role':
      return '역할';
    default:
      return key;
  }
};

const mergeMenuPermissions = (
  catalog: MenuPermissionRow[],
  effective: {
    menuCode: string;
    effectivePermission: MenuAccessLevel;
    overridden?: boolean;
  }[],
): MenuPermissionRow[] => {
  const byCode = new Map(effective.map((item) => [item.menuCode, item]));
  return catalog.map((row) => {
    const item = byCode.get(row.id);
    if (!item) return { ...row };
    // overridden=false → 상속(null). UI는 유효값을 보여 주되 저장 시 null로 보낸다.
    if (item.overridden === false) {
      return {
        ...row,
        level: item.effectivePermission,
        inherited: true,
      };
    }
    return {
      ...row,
      level: item.effectivePermission,
      inherited: false,
    };
  });
};

export const SettingsAccountDetailPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { readOnly } = useMenuAccess();
  const currentUser = useAuthStore((s) => s.user);
  const { accountId } = useParams({ strict: false }) as { accountId?: string };
  const numericId = Number(accountId);
  const validId =
    Number.isFinite(numericId) && numericId > 0 ? numericId : null;

  const [activeTab, setActiveTab] = useState<DetailTab>('basic');

  const {
    data: detail,
    isLoading,
    isError,
    error,
    refetch,
  } = useAdminUserDetail(validId);

  // 탭 활성화 시에만 관련 API 호출
  const {
    data: menuPermData,
    isLoading: menuPermLoading,
    refetch: refetchMenuPerms,
  } = useAdminUserMenuPermissions(validId, activeTab === 'permissions');
  const { data: auditLogs = [], isLoading: auditLoading } =
    useAdminUserAuditLogs(validId, activeTab === 'activity');
  const { data: accessLogPage, isLoading: accessLogLoading } =
    useUserAccessLogList(
      {
        userId: validId ?? undefined,
        userType: 'ADMIN',
        page: 0,
        size: 50,
      },
      validId != null && activeTab === 'activity',
    );
  const accessLogs = accessLogPage?.content ?? [];

  const updateMutation = useUpdateAdminUser();
  const removeMutation = useRemoveAdminUser();
  const unlockMutation = useUnlockAdminUser();
  const resetPasswordMutation = useResetAdminUserTemporaryPassword();
  const savePermsMutation = useSaveAdminUserMenuPermissions();

  const [baseline, setBaseline] = useState<BasicFormState | null>(null);
  const [form, setForm] = useState<BasicFormState | null>(null);
  const [permissions, setPermissions] = useState<MenuPermissionRow[]>(() =>
    ADMIN_MENU_CATALOG.map((row) => ({ ...row })),
  );
  const [permBaseline, setPermBaseline] = useState(() =>
    ADMIN_MENU_CATALOG.map((row) => ({ ...row })),
  );
  const [menuPermsHydrated, setMenuPermsHydrated] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetResultOpen, setResetResultOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [expandedAuditId, setExpandedAuditId] = useState<number | null>(null);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  // 계정 전환 시에만 기본 탭으로 초기화
  useEffect(() => {
    setActiveTab('basic');
    setMenuPermsHydrated(false);
    setExpandedAuditId(null);
    setPermissions(ADMIN_MENU_CATALOG.map((row) => ({ ...row })));
    setPermBaseline(ADMIN_MENU_CATALOG.map((row) => ({ ...row })));
  }, [validId]);

  useEffect(() => {
    if (!detail) return;
    const next = toBasicForm(detail);
    setBaseline(next);
    setForm(next);
  }, [detail]);

  useEffect(() => {
    if (!menuPermData) return;
    const merged = mergeMenuPermissions(
      ADMIN_MENU_CATALOG,
      menuPermData.permissions.map((p) => ({
        menuCode: p.menuCode,
        effectivePermission: p.effectivePermission,
        overridden: p.overridden,
      })),
    );
    setPermissions(merged);
    setPermBaseline(merged.map((row) => ({ ...row })));
    setMenuPermsHydrated(true);
  }, [menuPermData]);

  const account = detail ? mapAdminUserDetailToRow(detail) : null;
  const accountStatus: AdminAccountStatus = detail?.displayStatus ?? 'ACTIVE';
  const isLocked = accountStatus === 'LOCKED';
  /** 본인 계정 — 기본정보·메뉴권한·보안 액션 불가 (내 프로필에서만 수정) */
  const isSelfAccount = Boolean(
    detail &&
      currentUser &&
      ((currentUser.email &&
        detail.email.toLowerCase() === currentUser.email.toLowerCase()) ||
        (currentUser.username && detail.username === currentUser.username)),
  );
  const currentRole = form?.role ?? account?.role ?? '';
  const normalizedRole = normalizeAuthRole(currentRole);
  const roleProtected = normalizedRole === 'ROLE_MASTER';
  /** 마스터·관리만 면제 안내. 일반(ROLE_GENERAL)은 문구를 숨긴다. */
  const fullAccessPermissionHint = isMaster(currentRole)
    ? '마스터 역할은 전체 메뉴 접근이 가능하며, 개별 메뉴 권한을 저장하지 않습니다.'
    : normalizedRole === 'ROLE_MANAGER'
      ? '관리 계정은 전체 메뉴 접근이 가능하며, 개별 메뉴 권한을 저장하지 않습니다.'
      : null;
  /** 마스터·관리 면제, 또는 본인 계정·메뉴 READ 전용은 메뉴 권한 수정 불가 */
  const permissionsDisabled =
    isMenuPermissionExemptRole(currentRole) || isSelfAccount || readOnly;
  const basicEditDisabled = isSelfAccount || readOnly;
  const allowManagerRole = isMaster(currentUser?.role);

  const dirtyFields = useMemo(() => {
    if (!form || !baseline) return [];
    const keys: (keyof BasicFormState)[] = [
      'name',
      'phone',
      'department',
      'enabled',
      'role',
    ];
    return keys.filter((key) => form[key] !== baseline[key]);
  }, [form, baseline]);

  const permDirty = useMemo(() => {
    if (permissions.length !== permBaseline.length) return true;
    return permissions.some(
      (row, index) => row.level !== permBaseline[index]?.level,
    );
  }, [permissions, permBaseline]);

  const roleOptions = useMemo(() => {
    let options = [...EDITABLE_ROLE_OPTIONS];
    // 관리 롤 계정은 관리(ROLE_MANAGER)로 변경 불가 — 현재가 관리면 표시 유지
    if (!allowManagerRole) {
      const isCurrentManager =
        normalizeAuthRole(currentRole) === 'ROLE_MANAGER';
      options = options.filter(
        (o) => o.value !== 'ROLE_MANAGER' || isCurrentManager,
      );
    }
    const normalized = normalizeAuthRole(currentRole) as AuthRoleCode;
    if (
      normalized === 'ROLE_MASTER' &&
      !options.some((o) => o.value === normalized)
    ) {
      options.unshift({
        value: normalized,
        label: getRoleDisplayLabel(normalized),
      });
    }
    return options;
  }, [allowManagerRole, currentRole]);

  const headerStatus: AdminAccountStatus = form
    ? form.enabled
      ? accountStatus === 'TEMPORARY_PASSWORD' || accountStatus === 'LOCKED'
        ? accountStatus
        : accountStatus === 'DELETED'
          ? 'DELETED'
          : 'ACTIVE'
      : 'INACTIVE'
    : accountStatus;

  const handleConflict = async () => {
    toast.error(OPTIMISTIC_LOCK_CONFLICT_MESSAGE);
    await refetch();
    if (activeTab === 'permissions' || menuPermsHydrated) {
      await refetchMenuPerms();
    }
  };

  /**
   * 기본정보 저장용 권한 payload.
   * 마스터·관리는 빈 배열, 일반은 권한 탭/서버 값 유지.
   */
  const resolvePermissionsForBasicSave = async (
    roleName: ReturnType<typeof toAdminApiRoleName>,
  ) => {
    if (isMenuPermissionExemptRole(roleName)) return [];
    if (menuPermsHydrated) return toMenuPermissionSaveItems(permissions);
    const current = await adminUserService.getMenuPermissions(detail!.id);
    return current.permissions.map((item) => ({
      menuCode: item.menuCode,
      permissionLevel:
        item.effectivePermission === 'NONE' ? null : item.effectivePermission,
    }));
  };

  if (validId == null) {
    return (
      <div className="space-y-4">
        <EmptyState description="계정을 찾을 수 없습니다." />
        <Button
          variant="outline"
          onClick={() =>
            navigate({ to: ROUTE_PATHS.SETTINGS_ACCOUNTS as never })
          }
        >
          나가기
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner tip="계정 정보를 불러오는 중..." />;
  }

  if (!detail || !form || !baseline || !account) {
    return (
      <div className="space-y-4">
        <EmptyState description="계정을 찾을 수 없습니다." />
        <Button
          variant="outline"
          onClick={() =>
            navigate({ to: ROUTE_PATHS.SETTINGS_ACCOUNTS as never })
          }
        >
          나가기
        </Button>
      </div>
    );
  }

  const updateForm = <K extends keyof BasicFormState>(
    key: K,
    value: BasicFormState[K],
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleUnlock = async () => {
    try {
      await unlockMutation.mutateAsync({
        id: detail.id,
        body: { rowVersion: detail.rowVersion },
      });
      toast.success(`${detail.email} 계정 잠금이 해제되었습니다.`);
      await refetch();
    } catch (err) {
      if (isOptimisticLockConflict(err)) {
        await handleConflict();
        return;
      }
      handleApiError(err, toast);
    }
  };

  const handleUnlockAndResetPassword = async () => {
    try {
      await unlockMutation.mutateAsync({
        id: detail.id,
        body: { rowVersion: detail.rowVersion },
      });
      await refetch();
      setResetPasswordOpen(true);
    } catch (err) {
      if (isOptimisticLockConflict(err)) {
        await handleConflict();
        return;
      }
      handleApiError(err, toast);
    }
  };

  const handleConfirmResetPassword = async () => {
    try {
      // unlock 직후 refetch된 rowVersion 사용
      const latest = await refetch();
      const rowVersion = latest.data?.rowVersion ?? detail.rowVersion;
      const result = await resetPasswordMutation.mutateAsync({
        id: detail.id,
        body: { rowVersion },
      });
      setResetPasswordOpen(false);
      setTemporaryPassword(result.temporaryPassword);
      setResetResultOpen(true);
      await refetch();
    } catch (err) {
      if (isOptimisticLockConflict(err)) {
        await handleConflict();
        return;
      }
      handleApiError(err, toast);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await removeMutation.mutateAsync({
        id: detail.id,
        body: {
          confirmEmail: detail.email,
          rowVersion: detail.rowVersion,
        },
      });
      setDeleteAccountOpen(false);
      toast.success(`${detail.email} 계정이 삭제되었습니다.`);
      navigate({ to: ROUTE_PATHS.SETTINGS_ACCOUNTS as never });
    } catch (err) {
      if (isOptimisticLockConflict(err)) {
        await handleConflict();
        setDeleteAccountOpen(false);
        return;
      }
      handleApiError(err, toast);
    }
  };

  const handleCancelBasic = () => {
    setForm({ ...baseline });
  };

  const handleSaveBasic = async () => {
    if (basicEditDisabled) {
      toast.info('본인 계정은 내 프로필에서만 수정할 수 있습니다.');
      return;
    }
    if (!form.name.trim()) {
      toast.error('이름을 입력해주세요.');
      return;
    }

    const roleName = toAdminApiRoleName(form.role);

    try {
      const permissionsPayload = await resolvePermissionsForBasicSave(roleName);
      await updateMutation.mutateAsync({
        id: detail.id,
        body: {
          name: form.name.trim(),
          phone: form.phone.trim() || undefined,
          department: form.department.trim() || undefined,
          roleName,
          status: form.enabled ? 'ACTIVE' : 'INACTIVE',
          permissions: permissionsPayload,
          rowVersion: detail.rowVersion,
        },
      });
      toast.success('변경사항이 저장되었습니다.');
      setBaseline({ ...form });
      await refetch();
      if (activeTab === 'permissions' || menuPermsHydrated) {
        await refetchMenuPerms();
      }
    } catch (err) {
      if (isOptimisticLockConflict(err)) {
        await handleConflict();
        return;
      }
      handleApiError(err, toast);
    }
  };

  const handleCancelPermissions = () => {
    setPermissions(permBaseline.map((row) => ({ ...row })));
  };

  const handleSavePermissions = async () => {
    if (isSelfAccount) {
      toast.info('본인 계정의 메뉴 권한은 이 화면에서 수정할 수 없습니다.');
      return;
    }
    if (isMenuPermissionExemptRole(currentRole)) {
      toast.info('이 역할은 메뉴 권한을 저장할 수 없습니다.');
      return;
    }
    try {
      await savePermsMutation.mutateAsync({
        id: detail.id,
        permissions: toMenuPermissionSaveItems(permissions),
      });
      toast.success(
        '메뉴 권한이 저장되었습니다. 대상 계정의 기존 세션은 만료됩니다.',
      );
      setPermBaseline(permissions.map((row) => ({ ...row })));
      await refetchMenuPerms();
    } catch (err) {
      if (isOptimisticLockConflict(err)) {
        await handleConflict();
        return;
      }
      handleApiError(err, toast);
    }
  };

  const setPermissionLevel = (id: string, level: MenuAccessLevel) => {
    setPermissions((prev) => {
      const next = applyMenuPermissionChange(prev, id, level);
      return next.map((row, index) =>
        row.level !== prev[index]?.level ? { ...row, inherited: false } : row,
      );
    });
  };

  return (
    <div className="space-y-5">
      <ToolbarRow justify="between" gap={8}>
        <ToolbarRow className="gap-2.5">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {detail.name}
          </h1>
          <Badge variant={getRoleBadgeVariant(currentRole)}>
            {getRoleDisplayLabel(currentRole)}
          </Badge>
          <Badge variant={getAccountStatusBadgeVariant(headerStatus)}>
            {getAccountStatusLabel(headerStatus)}
          </Badge>
        </ToolbarRow>
        <ToolbarRow gap={8}>
          {isSelfAccount ? (
            <p className="text-sm text-muted-foreground">
              본인 계정은 내 프로필에서 수정하세요
            </p>
          ) : (
            <MenuWriteGate>
              {isLocked ? (
                <>
                  <Button
                    className="ruda-button"
                    onClick={() => void handleUnlock()}
                  >
                    잠금 해제
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => void handleUnlockAndResetPassword()}
                  >
                    잠금 해제 + 비밀번호 초기화
                  </Button>
                  <Button
                    variant="outline"
                    className="border-destructive/25 bg-ruda-error-50 text-ruda-error hover:bg-ruda-error-50/80 hover:text-ruda-error"
                    onClick={() => setDeleteAccountOpen(true)}
                  >
                    계정 삭제
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setResetPasswordOpen(true)}
                  >
                    비밀번호 초기화
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteAccountOpen(true)}
                  >
                    계정 삭제
                  </Button>
                </>
              )}
            </MenuWriteGate>
          )}
        </ToolbarRow>
      </ToolbarRow>

      <div className="flex gap-1 border-b border-border/60">
        {DETAIL_TABS.map((tab) => (
          <Button
            key={tab.key}
            type="button"
            variant={activeTab === tab.key ? 'default' : 'ghost'}
            size="sm"
            className="rounded-b-none"
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {isLocked && activeTab === 'basic' ? (
        <AccountLockedBanner
          message={formatAccountLockedBanner({
            lockedAt: account.lockedAt,
            failedAuthenticationCount: account.failedAuthenticationCount,
            lastLoginAt: account.lastLoginAt,
            lastLoginIp: account.lastLoginIp,
          })}
          onViewActivityLog={() => setActiveTab('activity')}
        />
      ) : null}

      <div hidden={activeTab !== 'basic'}>
        <div className="space-y-4">
          {isSelfAccount ? (
            <div className="flex items-start gap-2 rounded-12 bg-ruda-info-50 px-3.5 py-2.5 text-sm text-ruda-info">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <p>
                본인 계정은 계정 관리에서 수정할 수 없습니다. 이름·연락처는{' '}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() =>
                    navigate({ to: ROUTE_PATHS.MY_ACCOUNT as never })
                  }
                >
                  내 프로필
                </button>
                에서 변경하세요.
              </p>
            </div>
          ) : null}

          <DataCard className="p-6">
            <div className="space-y-5">
              <p className="text-xs font-bold text-primary">
                {isSelfAccount ? '기본 정보' : '편집 가능 항목'}
              </p>

              <FormFieldRow label="이름" htmlFor="account-name" required>
                <Input
                  id="account-name"
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                  className="max-w-[380px]"
                  disabled={basicEditDisabled}
                />
              </FormFieldRow>

              <FormFieldRow label="연락처" htmlFor="account-phone">
                <Input
                  id="account-phone"
                  value={form.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  className="max-w-[380px]"
                  disabled={basicEditDisabled}
                />
              </FormFieldRow>

              <FormFieldRow label="소속" htmlFor="account-dept">
                <Input
                  id="account-dept"
                  value={form.department}
                  onChange={(e) => updateForm('department', e.target.value)}
                  className="max-w-[380px]"
                  disabled={basicEditDisabled}
                />
              </FormFieldRow>

              <FormFieldRow label="상태">
                <div className="flex flex-wrap items-center gap-3">
                  <Select
                    value={form.enabled ? 'ACTIVE' : 'INACTIVE'}
                    onValueChange={(value) =>
                      updateForm('enabled', value === 'ACTIVE')
                    }
                    disabled={basicEditDisabled || isLocked}
                  >
                    <SelectTrigger className="w-[160px]" aria-label="계정 상태">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">활성</SelectItem>
                      <SelectItem value="INACTIVE">비활성</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {isSelfAccount
                      ? '본인 계정은 수정할 수 없습니다'
                      : readOnly
                        ? '조회 전용입니다'
                        : isLocked
                          ? '잠금 해제 후 변경 가능'
                          : '비활성 전환 시 즉시 로그인 차단'}
                  </p>
                </div>
              </FormFieldRow>

              <FormFieldRow label="역할">
                <div className="flex flex-wrap items-center gap-3">
                  <Select
                    value={normalizeAuthRole(form.role) || form.role}
                    onValueChange={(value) => updateForm('role', value)}
                    disabled={basicEditDisabled || roleProtected}
                  >
                    <SelectTrigger className="w-[380px] max-w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {isSelfAccount
                      ? '본인 계정은 수정할 수 없습니다'
                      : readOnly
                        ? '조회 전용입니다'
                        : roleProtected
                          ? '마스터·관리 역할은 읽기 전용입니다'
                          : '관리 / 일반만 변경 가능'}
                  </p>
                </div>
              </FormFieldRow>

              <div className="h-px w-full bg-border" />

              <p className="text-xs font-bold text-muted-foreground">
                읽기 전용 항목
              </p>

              <FormFieldRow label="유저네임">
                <ReadOnlyField value={detail.username} />
              </FormFieldRow>

              <FormFieldRow label="계정 ID (이메일)">
                <div className="flex flex-wrap items-center gap-3">
                  <ReadOnlyField value={detail.email} />
                  <p className="text-[11px] text-muted-foreground">수정 불가</p>
                </div>
              </FormFieldRow>

              <FormFieldRow label="생성일 / 생성자">
                <ReadOnlyField
                  value={`${formatAdminDateTime(detail.createdAt, false) ?? detail.createdAt} · ${
                    detail.createdBy != null ? String(detail.createdBy) : '—'
                  }`}
                />
              </FormFieldRow>

              <FormFieldRow label="임시 비밀번호 만료">
                <ReadOnlyField
                  value={
                    formatAdminDateTime(detail.temporaryPasswordExpiresAt) ??
                    '—'
                  }
                />
              </FormFieldRow>
            </div>
          </DataCard>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-h-5 items-center gap-1.5">
              {!basicEditDisabled && dirtyFields.length > 0 ? (
                <>
                  <span className="size-1.5 shrink-0 rounded-full bg-ruda-warning" />
                  <p className="text-xs text-muted-foreground">
                    저장되지 않은 변경사항 {dirtyFields.length}건 —{' '}
                    {dirtyFields.map(fieldLabel).join(', ')}
                  </p>
                </>
              ) : null}
            </div>
            {!basicEditDisabled ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancelBasic}
                  disabled={dirtyFields.length === 0}
                >
                  변경 취소
                </Button>
                <Button
                  className="ruda-button"
                  onClick={() => void handleSaveBasic()}
                  disabled={
                    dirtyFields.length === 0 || updateMutation.isPending
                  }
                >
                  변경사항 저장
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div hidden={activeTab !== 'permissions'}>
        <div className="space-y-4">
          {isSelfAccount ? (
            <div className="flex items-start gap-2 rounded-12 bg-ruda-info-50 px-3.5 py-2.5 text-sm text-ruda-info">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <p>본인 계정의 메뉴 권한은 이 화면에서 수정할 수 없습니다.</p>
            </div>
          ) : fullAccessPermissionHint ? (
            <div className="flex items-start gap-2 rounded-12 bg-ruda-info-50 px-3.5 py-2.5 text-sm text-ruda-info">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <p>{fullAccessPermissionHint}</p>
            </div>
          ) : null}

          {menuPermLoading && !menuPermsHydrated ? (
            <LoadingSpinner tip="메뉴 권한을 불러오는 중..." />
          ) : (
            <>
              <DataCard
                className={cn(
                  permissionsDisabled && 'pointer-events-none opacity-50',
                )}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40%]">메뉴</TableHead>
                      <TableHead className="text-center">없음</TableHead>
                      <TableHead className="text-center">조회</TableHead>
                      <TableHead className="text-center">편집</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {permissions.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell
                          className={cn(
                            row.child
                              ? 'pl-8 font-normal text-muted-foreground'
                              : 'font-medium text-foreground',
                          )}
                        >
                          {row.child ? `└  ${row.label}` : row.label}
                        </TableCell>
                        <TableCell colSpan={3} className="p-0">
                          <RadioGroup
                            value={row.level}
                            onValueChange={(value) =>
                              setPermissionLevel(
                                row.id,
                                value as MenuAccessLevel,
                              )
                            }
                            className="grid grid-cols-3 gap-0"
                            disabled={permissionsDisabled}
                          >
                            {(
                              ['NONE', 'READ', 'WRITE'] as MenuAccessLevel[]
                            ).map((level) => (
                              <div
                                key={level}
                                className="flex items-center justify-center py-3.5"
                              >
                                <RadioGroupItem
                                  value={level}
                                  id={`${row.id}-${level}`}
                                  aria-label={`${row.label} ${
                                    level === 'NONE'
                                      ? '없음'
                                      : level === 'READ'
                                        ? '조회'
                                        : '편집'
                                  }`}
                                />
                              </div>
                            ))}
                          </RadioGroup>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataCard>

              <div className="flex items-start gap-2 rounded-12 bg-ruda-info-50 px-3.5 py-2.5 text-sm text-ruda-info">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <p>
                  하위 메뉴는 기본적으로 상위 메뉴 설정을 상속하며, 개별 변경 시
                  상속이 해제됩니다. 편집 권한은 조회 권한을 포함합니다.
                </p>
              </div>

              <MenuWriteGate>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCancelPermissions}
                    disabled={!permDirty || permissionsDisabled}
                  >
                    취소
                  </Button>
                  <Button
                    className="ruda-button"
                    onClick={() => void handleSavePermissions()}
                    disabled={
                      !permDirty ||
                      permissionsDisabled ||
                      savePermsMutation.isPending
                    }
                  >
                    권한 저장
                  </Button>
                </div>
              </MenuWriteGate>
            </>
          )}
        </div>
      </div>

      <div hidden={activeTab !== 'activity'}>
        <div className="space-y-5">
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-foreground">접속 이력</h2>
            <DataCard>
              {accessLogLoading ? (
                <LoadingSpinner tip="접속 로그를 불러오는 중..." />
              ) : accessLogs.length === 0 ? (
                <EmptyState description="접속 이력이 없습니다." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>일시</TableHead>
                      <TableHead>유형</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>위치</TableHead>
                      <TableHead>결과</TableHead>
                      <TableHead>실패 사유</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accessLogs.map((row) => (
                      <AccessLogTableRow key={row.id} log={row} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </DataCard>
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-bold text-foreground">
              계정 변경 이력
            </h2>
            <DataCard>
              {auditLoading ? (
                <LoadingSpinner tip="감사 로그를 불러오는 중..." />
              ) : auditLogs.length === 0 ? (
                <EmptyState description="계정 변경 이력이 없습니다." />
              ) : (
                <ul className="divide-y divide-border">
                  {auditLogs.map((log) => (
                    <AuditLogListItem
                      key={log.id}
                      log={log}
                      expanded={expandedAuditId === log.id}
                      onToggle={() =>
                        setExpandedAuditId((prev) =>
                          prev === log.id ? null : log.id,
                        )
                      }
                    />
                  ))}
                </ul>
              )}
            </DataCard>
          </div>
        </div>
      </div>

      <PasswordResetConfirmModal
        open={resetPasswordOpen}
        email={detail.email}
        name={detail.name}
        onClose={() => setResetPasswordOpen(false)}
        onConfirm={() => void handleConfirmResetPassword()}
      />
      <PasswordResetResultModal
        open={resetResultOpen}
        email={detail.email}
        temporaryPassword={temporaryPassword}
        onClose={() => {
          setResetResultOpen(false);
          setTemporaryPassword('');
        }}
      />
      <AccountDeleteConfirmModal
        open={deleteAccountOpen}
        email={detail.email}
        name={detail.name}
        onClose={() => setDeleteAccountOpen(false)}
        onConfirm={() => void handleConfirmDelete()}
      />
    </div>
  );
};

type AuditLogListItemProps = {
  log: AdminAuditLog;
  expanded: boolean;
  onToggle: () => void;
};

const AccessLogTableRow: React.FC<{ log: UserAccessLog }> = ({ log }) => {
  const accessTypeLabel =
    log.accessType === 'LOGIN'
      ? '로그인'
      : log.accessType === 'TOKEN_REFRESH'
        ? '토큰 갱신'
        : '토큰 접근';

  return (
    <TableRow>
      <TableCell className="text-muted-foreground">
        {formatAdminDateTime(log.accessedAt, true, true)}
      </TableCell>
      <TableCell className="text-muted-foreground">{accessTypeLabel}</TableCell>
      <TableCell className="text-muted-foreground">
        {log.clientIp ?? '—'}
      </TableCell>
      <TableCell
        className="text-muted-foreground"
        title="브라우저가 제공한 참고 값이며 위조될 수 있습니다. 감사 기준은 IP입니다."
      >
        {formatAccessLogLocation(log.latitude, log.longitude)}
      </TableCell>
      <TableCell>
        <Badge variant={log.status === 'SUCCESS' ? 'success' : 'destructive'}>
          {log.status === 'SUCCESS' ? '성공' : '실패'}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {log.failReason || '—'}
      </TableCell>
    </TableRow>
  );
};

const AuditLogListItem: React.FC<AuditLogListItemProps> = ({
  log,
  expanded,
  onToggle,
}) => {
  const at = formatAdminDateTime(log.occurredAt) ?? log.occurredAt;
  const eventLabel = getAuditEventLabel(log.eventType as AuditEventType);
  const actor = log.actorUsername ?? '—';

  return (
    <li className="px-5 py-3.5">
      <button
        type="button"
        className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
        onClick={onToggle}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className="text-xs text-muted-foreground">{at}</span>
          <Badge variant="secondary">{eventLabel}</Badge>
          <span className="truncate text-sm text-foreground">
            {formatAuditChangesSummary(log, 80)}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">수행: {actor}</span>
      </button>
      {expanded ? (
        <div className="mt-3 rounded-12 bg-muted/50 p-3">
          <AuditChangesDetail
            changes={log.changes ?? []}
            beforeSnapshot={log.beforeSnapshot}
            afterSnapshot={log.afterSnapshot}
            compact
          />
        </div>
      ) : null}
    </li>
  );
};

type FormFieldRowProps = {
  label: string;
  htmlFor?: string;
  required?: boolean;
  children: React.ReactNode;
};

const FormFieldRow: React.FC<FormFieldRowProps> = ({
  label,
  htmlFor,
  required,
  children,
}) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
    <label
      htmlFor={htmlFor}
      className="w-[200px] shrink-0 text-[15px] font-medium text-foreground"
    >
      {label}
      {required ? (
        <span className="ml-0.5 text-destructive" aria-hidden>
          *
        </span>
      ) : null}
    </label>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

const ReadOnlyField: React.FC<{ value: string }> = ({ value }) => (
  <div className="flex max-w-[380px] items-center justify-between gap-2 rounded-12 border border-border bg-muted/60 px-3 py-2.5 text-sm text-muted-foreground">
    <span className="truncate">{value}</span>
    <Lock className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
  </div>
);
